import {SupportLanguage, SupportLanguageMap} from '@di-shared/i18init';
import dateFnsFormat from 'date-fns/format';
import {getLanguage} from '@di-shared/features/common/useInferableTranslation';

const DASHED_DATE_FORMAT_MAP: SupportLanguageMap<string> = {
  bg: 'dd-MM-yyyy', // Bulgaria
  cs: 'dd-MM-yyyy', // Czech Republic
  da: 'dd-MM-yyyy', // Denmark
  de: 'dd-MM-yyyy', // Germany
  el: 'dd-MM-yyyy', // Greece
  en: 'MM-dd-yyyy',
  en_GB: 'dd-MM-yyyy',
  en_US: 'MM-dd-yyyy',
  es: 'dd-MM-yyyy', // Spain
  et: 'dd-MM-yyyy', // Estonia
  fi: 'dd-MM-yyyy', // Finland
  fr: 'dd-MM-yyyy', // France
  hr: 'dd-MM-yyyy', // Croatia
  hu: 'yyyy-MM-dd', // Hungary
  it: 'dd-MM-yyyy', // Italy
  ko: 'yyyy-MM-dd',
  lt: 'yyyy-MM-dd', // Lithuanian
  lv: 'dd-MM-yyyy', // Latvian
  nl: 'dd-MM-yyyy', // Netherlands
  no: 'dd-MM-yyyy', // Norway
  pl: 'dd-MM-yyyy', // Poland
  pt: 'dd-MM-yyyy', // Portugal
  ro: 'dd-MM-yyyy', // Romania
  sk: 'dd-MM-yyyy', // Slovakia
  sl: 'dd-MM-yyyy', // Slovenia
  sv: 'yyyy-MM-dd', // Sweden
  uk: 'dd-MM-yyyy', // United Kingdom
  tr: 'dd-MM-yyyy', // Turkey
} as const;

function useDashedDatetimeFormatter() {
  const language = getLanguage();

  /**
   * date: ex) 2023-08-18
   * dateDay: ex) 2023-08-18 (수)
   * datetime: ex) 2023-08-18, 13:23
   * time: ex) 13:23
   */

  const DATETIME_FORMAT_HELPER = {
    datetime: getDatetimeFormat,
    dateDay: getDateDayFormat,
    date: getDateFormat,
    time: getTimeFormat,
  } as const;

  type DatetimeFormat = keyof typeof DATETIME_FORMAT_HELPER;

  function format(date: Date, format: DatetimeFormat) {
    return dateFnsFormat(date, DATETIME_FORMAT_HELPER[format](language));
  }

  function formatRange(dateFrom: Date, dateTo: Date, format: DatetimeFormat) {
    const from = dateFnsFormat(dateFrom, DATETIME_FORMAT_HELPER[format](language));
    const to = dateFnsFormat(dateTo, DATETIME_FORMAT_HELPER[format](language));
    return `${from} ~ ${to}`;
  }

  function getDateFormat(locale: SupportLanguage) {
    return DASHED_DATE_FORMAT_MAP[locale];
  }

  function getDateDayFormat(locale: SupportLanguage) {
    return `${getDateFormat(locale)} (eee)`;
  }

  function getTimeFormat() {
    return 'HH:mm';
  }

  function getDatetimeFormat(locale: SupportLanguage) {
    return `${getDateFormat(locale)}, ${getTimeFormat()}`;
  }

  return {
    format,
    formatRange,
    getDateFormat,
    getDateDayFormat,
    getTimeFormat,
    getDatetimeFormat,
  };
}

export default useDashedDatetimeFormatter;
